import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  capitalizeFirstLetter,
  formatDateDash,
  formatOutsidePermittedQuantityTitle,
  getFirstNumberFromString,
  handleCatch,
  isDispensingMoreThan3DaysBetween2Dates,
  isDispensingMoreThan7DaysBetween2Dates,
  isObject,
  pluralizeUnit,
} from "../../../shared/helpers";
import { PafFormCard } from "./layout/PafFormCard";
import { PAF_PAGE_CLASS } from "./layout/pafPageClass";
import { sortPrescriptionsByDateDesc } from "./utils/pafPrescriptionHelpers";
import {
  canInpatientPharmacistEditPaf,
  isInpatientPharmacyEditablePafStatus,
  normalizePafPrescription,
} from "../../common/ViewPAF/utils/viewPafFormLogic";
import { shouldShowPAFAgeWarningModal } from "./utils/pafAgeWarningLogic";
import { PafAgeWarningModal } from "./modals/PafAgeWarningModal";
import { PafFooterActions } from "./layout/PafFooterActions";
import { PafFormScrollBody } from "./layout/PafFormScrollBody";
import { PafPageModals } from "./modals/PafPageModals";
import PrescriptionReviewModal from "../../common/ViewPAF/components/PrescriptionReviewModal";

// import { ReactComponent as DownloadBlue } from "../../../assets/icons/downloadBlue.svg";
import { App } from "../../../routes/Constants";
import APIVariables, {
  buildHcpBrochurePath
} from "../../../services/api-variables";
import { typeOfPAF } from "../../../constants/typeOfPAF";
import Loader from "../../common/Loader/Loader";
import { errorMessages } from "../../../constants/errorMessages.constants";
import NominateDispenser from "./modals/NominateDispenser";
import MahSelection from "./modals/MahSelection";
import useRoles from "../../../hooks/useRoles";
import BrochureWithText from "../Brochure/BrochureWithText";
import Axios from "../../../services/Axios";

// PAF route: load prescriptions, open the selected PAF, run approve/reject/dispense by role.
const PrescriptionAuthorisationForm = () => {
  const patientID = window.location.pathname.split("/")[2];
  const pafID = window.location.pathname.split("/")[3];

  const {
    isPrescriber,
    isConnectedNurse,
    isHomecare,
    isPharmacy,
    isOutpatient,
    storageData,
    canReviewPaf,
  } = useRoles();
  const [userInfo, setUserInfo] = useState([]);
  const [items, setItems] = useState([]);
  const prescriptionsSortedByDate = useMemo(
    () => sortPrescriptionsByDateDesc(items),
    [items]
  );
  // Newest row = current PAF; rest populate Treatment Overview history.
  const latestPafId = prescriptionsSortedByDate[0]?.id;
  const historyPafSummaries = prescriptionsSortedByDate.slice(1);
  const [unchagedItems, setUnchagedItems] = useState([]);
  const [errorMessage, setErrorMessage] = useState(""); //errors from backend
  const navigate = useNavigate();
  const location = useLocation();

  const downloadPafPdf = useCallback(() => {
    if (!patientID) return Promise.resolve();
    return Axios.get(APIVariables.DOWNLOAD_PAF + patientID)
      .then((response) => response.data.report)
      .then(async (base64) => {
        const base64Pdf = await fetch(`data:application/pdf;base64,${base64}`);
        return base64Pdf.blob();
      })
      .then((blob) => {
        const file = new Blob([blob], { type: "application/pdf" });
        const fileURL = URL.createObjectURL(file);
        const downloadLink = document.createElement("a");
        downloadLink.href = fileURL;
        downloadLink.download = "PAF.pdf";
        downloadLink.click();
        downloadLink.remove();
        URL.revokeObjectURL(fileURL);
      })
      .catch((error) => {
        handleCatch(error);
      });
  }, [patientID]);

  const [quitModalIsOpen, setIsOpenQuit] = useState(false);
  const [statePAFModal, setStatePAFModal] = useState(false);
  const [message, setMessage] = useState({
    state: 1,
    type: "",
    title: "",
    description: "",
    buttonText: "",
  });
  const [isFormApproved, setIsFormApproved] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [form, setForm] = useState({
    reason_rejection: "",
    reason_rejection_input: "",
  });

  const [revert, setRevert] = useState({
    reason_revert: "",
    reason_revert_input: "",
  });
  const [detailsPAF, setDetailsPAF] = useState([]);
  const [activatedItem, setActivateTab] = useState([]);
  const activePafDetails =
    !Array.isArray(activatedItem) && activatedItem
      ? detailsPAF.find((d) => d.id === activatedItem) ?? detailsPAF[0]
      : detailsPAF[0];

  /** Active accordion row; falls back to first prescription when none expanded (initial / single-PAF). */
  const openedPAF = useMemo(() => {
    if (!detailsPAF?.length) {
      return {};
    }
    if (!activatedItem || Array.isArray(activatedItem)) {
      return detailsPAF[0] ?? {};
    }
    const found = detailsPAF.find((e) => e.id === activatedItem);
    return found ?? detailsPAF[0] ?? {};
  }, [activatedItem, detailsPAF]);

  const [showPAFAgeWarningModal, setShowPAFAgeWarningModal] = useState(false);
  const dismissedPAFAgeWarningIdsRef = useRef(new Set());
  const openedPAFAgeWarningIdsRef = useRef(new Set());
  const lastActivatedPAFWarningIdRef = useRef(null);
  const [aditionalOptions, setAditionalOptions] = useState({
    capsuleStrength: false,
    capsulePerCycle: false,
    numberOfCycles: false,
    numberOfWeeks: false,
  });

  // TODO: Check if this is needed
  // const isValidStrengths = isValidStrengthsForPaf(activePafDetails);
  // const isValidCapsules = isValidCapsulesForPaf(activePafDetails);
  // const isValidCycles = isValidCyclesForPaf(activePafDetails);
  // const isValidWeeksPerCycle = isValidWeeksPerCycleForPaf(activePafDetails);

  const [aditionalOptionsState, setAditionalOptionsState] = useState({
    showErrors: false,
    validation: {
      capsuleStrength: {
        isValid: !!openedPAF?.strengths?.map(
          (strength) => !!strength?.strength_value
        ),
      },
      capsulePerCycle: {
        isValid: !!openedPAF?.strengths?.map(
          (strength) => !!strength?.capsules
        ),
      },
      numberOfCycles: {
        isValid:
          openedPAF?.n_cycles === null
            ? !!openedPAF?.new_capsule_cycles?.id
              ? !!openedPAF?.new_capsule_cycles?.id
              : !!openedPAF?.n_weeks_other
            : !!openedPAF?.n_cycles,
      },

      numberOfWeeks: {
        isValid:
          openedPAF?.schedule_display_type === "weeks_per_cycle"
            ? Boolean(openedPAF?.weeks_per_cycle)
              ? true
              : Boolean(openedPAF?.new_weeks_per_cycle?.id)
                ? true
                : Boolean(openedPAF?.new_weeks_per_cycle_other)
                  ? true
                  : false
            : true,
      },
    },
    areErrors: true,
  });

  const isValidStrengths =
    openedPAF?.strengths?.length > 0
      ? openedPAF?.strengths?.every((strength) => {
        if (
          strength.strength_id === "Incomplete" &&
          Boolean(strength?.new_strength_id)
        ) {
          return true;
        } else if (Boolean(strength.strength_id)) {
          return true;
        } else {
          return false;
        }
      })
      : false;

  const isValidCapsules =
    openedPAF?.strengths?.length > 0
      ? openedPAF?.strengths?.every((strength) => {
        if (
          strength.capsules === "Incomplete" &&
          Boolean(strength?.new_capsules)
        ) {
          return true;
        } else if (
          Boolean(strength.capsules) &&
          strength.capsules !== "Incomplete"
        ) {
          return true;
        } else {
          return false;
        }
      })
      : false;

  const isValidCycles = Boolean(openedPAF?.n_cycles)
    ? true
    : Boolean(openedPAF?.new_capsule_cycles?.id)
      ? true
      : Boolean(openedPAF?.n_weeks_other)
        ? true
        : false;

  const isValidWeeksPerCycle = Boolean(openedPAF?.weeks_per_cycle)
    ? true
    : Boolean(openedPAF?.new_weeks_per_cycle?.id)
      ? true
      : Boolean(openedPAF?.new_weeks_per_cycle_other)
        ? true
        : false;

  useEffect(() => {
    setAditionalOptionsState((prevState) => ({
      ...prevState,
      validation: {
        capsuleStrength: {
          isValid: isValidStrengths,
        },
        capsulePerCycle: {
          isValid: isValidCapsules,
        },
        numberOfCycles: {
          isValid: isValidCycles,
        },
        numberWeeksPerCycle: {
          isValid:
            openedPAF?.schedule_display_type === "weeks_per_cycle"
              ? isValidWeeksPerCycle
              : true,
        },
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsPAF, activatedItem]);
  const [numberOfCylesValues, setNumberOfCylesValue] = useState([]);
  const [showNominateDispenser, setShowNominateDispenser] = useState(false);
  const [showMahSelection, setShowMahSelection] = useState(false);
  const [compliantPaf, setCompliantPAF] = useState(false);
  const [isApproveAndDispense, setIsApproveAndDispense] = useState(false);
  const [finishModalOpen, setFinishModalOpen] = useState(false);
  const [finishModalContent, setFinishModalContent] = useState({
    title: "",
    content: "",
    onFinish: () => true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isOtherNumberOfCycledValid, setIsOtherNumberOfCycledValid] =
    useState(false);
  const lastFormID =
    prescriptionsSortedByDate.length && prescriptionsSortedByDate[0].id;

  const [changeValuesModalIsOpen, setIChangeValuesModal] = useState({
    isOpen: false,
    title: "",
    description: "",
    typeOfError: "",
  });
  const [confirmWasChangedPregancyTest, setConfirmWasChangedPregancyTest] =
    useState(null);
  const [
    confirmWasPregancyTestIsNotValid,
    setConfirmWasPregancyTestIsNotValid,
  ] = useState(null);
  const [confirmWasApproved, setConfirmWasApproved] = useState(null);
  const [confirmWasDispensed7DaysAfter, setConfirmWasDispensed7DaysAfter] =
    useState(null);
  const [confirmWasChangedNumberOfCycles, setConfirmWasChangedNumberOfCyles] =
    useState(null);
  const [confirmWasChangedWeeksPerCycle, setConfirmWasChangedWeeksPerCycle] =
    useState(null);
  const [confirmChangesCapsStrength, setConfirmChangesCapsStrength] =
    useState(null);
  const [confirmChangesCapsPerCycle, setConfirmChangesCapsPerCycle] =
    useState(null);
  const [modalNonCompliantPAF, setModalNonCompliantPAF] = useState({
    isOpen: false,
    description: [],
    screen: 1,
  });

  const [isOperationInProgress, setIsOperationInProgress] = useState(false);

  // TODO: Extract each state for modal into a object
  const [
    confirmPregnancyTestDateIncompleteModal,
    setConfirmPregnancyTestDateIncompleteModal,
  ] = useState(null);
  const [
    confirmNumberOfCylesIsNotValidWarningModal,
    setConfirmNumberOfCylesIsNotValidWarningModal,
  ] = useState(null);
  // const [
  //   confirmNumberOfWeeksIsNotValidWarningModal,
  //   setConfirmNumberOfWeeksIsNotValidWarningModal,
  // ] = useState(null);
  const [
    confirmNumberOfWeeksIsNotValidAgainWarningModal,
    setConfirmNumberOfWeeksIsNotValidAgainWarningModal,
  ] = useState(null);
  const [
    confirmCapsulesPerWeeksIsNotValidWarningModal,
    setConfirmCapsulesPerWeeksIsNotValidWarningModal,
  ] = useState(null);
  const [
    confirmCheckboxIsNotValidWarningModal,
    setConfirmCheckboxIsNotValidWarningModal,
  ] = useState(null);
  const [
    confirmIndicationIncompleteModal,
    setConfirmIndicationIncompleteModal,
  ] = useState(null);

  const [modalNonCompliantPAFWarning, setModalNonCompliantPAFWarning] =
    useState({
      svg: null,
      isOpen: false,
      title: "",
      description: [],
      warningType: null,
    });
  const [validPassword, setValidPassword] = useState("");
  const [errorMessageFromServer, setErrorMessageFromServer] = useState([]);
  const isLastFormApprovedAvailableForRenew =
    prescriptionsSortedByDate &&
    prescriptionsSortedByDate.length &&
    (prescriptionsSortedByDate[0].status_name === "Approved" ||
      prescriptionsSortedByDate[0].status_name === "Dispensed" ||
      prescriptionsSortedByDate[0].status_name === "Rejected");
  const [errors, setErrors] = useState({});
  const [dispensingForm, setDispensingForm] = useState({
    type: null,
    location: null,
    confirmation_risk: false,
  });

  const [hcp, setHcp] = useState();
  const getHCP = async (productid) => {
    try {
      const data = await Axios.get(
        buildHcpBrochurePath(productid)
      );
      setHcp(data.data);
    } catch (error) {
      handleCatch(error);
    }
  };

  const setOpenedItem = (data) => {
    setDetailsPAF((prevDetailsPAF) => {
      const detailsPAFIndex = prevDetailsPAF.findIndex(
        (element) => element.id === data.id
      );

      if (detailsPAFIndex === -1) {
        return [...prevDetailsPAF, data];
      }

      const updatedDetailsPAF = [...prevDetailsPAF];
      updatedDetailsPAF[detailsPAFIndex] = {
        ...updatedDetailsPAF[detailsPAFIndex],
        ...data,
      };

      return updatedDetailsPAF;
    });
  };

  const getOpenedItem = useCallback(() => openedPAF, [openedPAF]);

  const handlePrescriptionReviewSuccess = useCallback(async () => {
    const prescriptionId = pafID || activePafDetails?.id;
    if (!prescriptionId) return;
    try {
      const res = await Axios.get(APIVariables.PRESCRIPTIONS_GET + prescriptionId);
      if (res?.status === 200) {
        const normalized = normalizePafPrescription(res.data);
        setDetailsPAF((prev) =>
          prev.map((item) => (String(item.id) === String(normalized.id) ? normalized : item))
        );
      }
    } catch (error) {
      handleCatch(error);
    }
  }, [pafID, activePafDetails?.id]);

  const [continuDispenseOverduePAF, setContinuDispenseOverduePAF] = useState({
    isConfirmedMainModal: false,
    isMainModalDisplayed: false,
    isValidNonComplaintHomecareAndOutPatient: false,
  });

  // Reset only 7-day dispensing warning state when switching PAFs. Do not clear
  // isValidNonComplaintHomecareAndOutPatient — that is set by validateOutpatientDispense
  // for Approved (etc.) homecare/outpatient and must survive this effect or Dispense stalls.
  useEffect(() => {
    if (!activatedItem || Array.isArray(activatedItem)) return;
    setContinuDispenseOverduePAF((prev) => ({
      ...prev,
      isConfirmedMainModal: false,
      isMainModalDisplayed: false,
    }));
  }, [activatedItem]);

  const [capsule_changes_tracker, setCapsule_changes_tracker] = useState({});

  useEffect(() => {
    getNumberOfCycles();
    // added window.location.pathname fixed a bug when finding a patient from top menu is not chanching the UI
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  useEffect(() => {
    getPAF();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientID]);

  // useEffect(() => {
  //   setIsFormApproved(null);
  // }, [items]);

  useEffect(() => {
    validateAditionalOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aditionalOptions]);

  useEffect(() => {
    if (!activatedItem || Array.isArray(activatedItem) || detailsPAF.length === 0) {
      lastActivatedPAFWarningIdRef.current = null;
      setShowPAFAgeWarningModal(false);
      return;
    }

    const activePafKey = String(activatedItem);
    const currentPAF = detailsPAF.find(
      (element) => String(element.id) === activePafKey
    );

    if (!currentPAF) {
      lastActivatedPAFWarningIdRef.current = null;
      return;
    }

    if (lastActivatedPAFWarningIdRef.current !== activePafKey) {
      dismissedPAFAgeWarningIdsRef.current.delete(activePafKey);
    }
    lastActivatedPAFWarningIdRef.current = activePafKey;

    if (dismissedPAFAgeWarningIdsRef.current.has(activePafKey)) {
      setShowPAFAgeWarningModal(false);
      return;
    }

    const shouldShow = shouldShowPAFAgeWarningModal(currentPAF, {
      isPharmacy,
    });

    if (shouldShow) {
      openedPAFAgeWarningIdsRef.current.add(activePafKey);
      setShowPAFAgeWarningModal(true);
      return;
    }

    if (openedPAFAgeWarningIdsRef.current.has(activePafKey)) {
      setShowPAFAgeWarningModal(true);
      return;
    }

    setShowPAFAgeWarningModal(false);
  }, [activatedItem, detailsPAF, isPharmacy]);

  const closePAFAgeWarningModal = () => {
    if (activatedItem && !Array.isArray(activatedItem)) {
      const activePafKey = String(activatedItem);
      dismissedPAFAgeWarningIdsRef.current.add(activePafKey);
      openedPAFAgeWarningIdsRef.current.delete(activePafKey);
    }
    setShowPAFAgeWarningModal(false);
  };

  useEffect(() => {
    dismissedPAFAgeWarningIdsRef.current = new Set();
    openedPAFAgeWarningIdsRef.current = new Set();
  }, [patientID]);

  const updateNotifications = () => {
    document.dispatchEvent(new CustomEvent("fetchNotifications"));
  };

  const [childObject, setChildObject] = useState(null);
  const updateChildObject = (newValue) => {
    const nextValue = [newValue];
    setChildObject((prevValue) => {
      if (JSON.stringify(prevValue) === JSON.stringify(nextValue)) {
        return prevValue;
      }

      return nextValue;
    });
  };
  useEffect(() => {
    if (!childObject?.length) return;
    const nextItem = childObject[0];
    if (!nextItem || Object.keys(nextItem).length === 0) return;

    const activePafKey = !Array.isArray(activatedItem) && activatedItem
      ? String(activatedItem)
      : String(nextItem.id || "");

    if (!activePafKey) return;

    setDetailsPAF((prevDetailsPAF) => {
      const activeIndex = prevDetailsPAF.findIndex(
        (element) => String(element.id) === activePafKey
      );

      if (activeIndex === -1) {
        if (!nextItem?.id) return prevDetailsPAF;
        return [...prevDetailsPAF, nextItem];
      }

      if (
        JSON.stringify(prevDetailsPAF[activeIndex]) === JSON.stringify(nextItem)
      ) {
        return prevDetailsPAF;
      }

      const updatedDetailsPAF = [...prevDetailsPAF];
      updatedDetailsPAF[activeIndex] = nextItem;
      return updatedDetailsPAF;
    });
  }, [activatedItem, childObject]);

  const validateAditionalOptions = () => {
    const validation = {
      capsuleStrength: {
        isValid: isValidStrengths,
      },
      capsulePerCycle: {
        isValid: isValidCapsules,
      },
      numberOfCycles: {
        isValid: isValidCycles,
      },
      numberWeeksPerCycle: {
        isValid:
          openedPAF?.schedule_display_type === "weeks_per_cycle"
            ? isValidWeeksPerCycle
            : true,
      },
    };

    let areErrors = Object.values(validation).some((field) => {
      return !field.isValid;
    });
    setAditionalOptionsState((prevState) => ({
      ...prevState,
      validation: validation,
      areErrors: areErrors,
    }));
  };

  function typeOfForm() {
    const patientType =
      userInfo.patient_type || getOpenedItem()?.patient?.patient_type;

    if (patientType === "Male") {
      return typeOfPAF.MALE;
    } else if (patientType === "Woman of Non-Childbearing Potential") {
      return typeOfPAF.WOMANOFNCP;
    } else {
      return typeOfPAF.WOMANOFCPRENEW2;
    }
  }

  const handleAditionalOptionsChange = (e, type = "input", name) => {
    if (type === "select") {
      setAditionalOptions({ ...aditionalOptions, [name]: e });
    } else {
      const {
        target: { name, value },
      } = e;

      setAditionalOptions({ ...aditionalOptions, [name]: value });
    }
  };

  const closeFinishModal = () => {
    setFinishModalOpen(false);
    setIsOperationInProgress(false);
  };

  const validateOutpatientDispense = (data) => {
    const status = data?.status ?? data?.status_name;
    // Match isDispensingPAF: WCBP + (Overdue | Status Update) must show 7-day modal first — no direct-dispense fast path.
    const wcbpNeedsSevenDayWarningFirst =
      data?.patient?.patient_type === "Woman of Childbearing Potential" &&
      (status === "Overdue" || status === "Status Update");

    const isValidtoBeDispensedPAFForHomecareOutPatient =
      data?.patient?.patient_type !== "Woman of Childbearing Potential" ||
      (data?.patient?.patient_type === "Woman of Childbearing Potential" &&
        !wcbpNeedsSevenDayWarningFirst);

    setContinuDispenseOverduePAF((prev) => ({
      ...prev,
      isValidNonComplaintHomecareAndOutPatient:
        !!isValidtoBeDispensedPAFForHomecareOutPatient,
    }));
  };

  const handleIsPharmaApproved = (data) => {
    const is_form_approved_pharmacy = !isInpatientPharmacyEditablePafStatus(data);

    if (is_form_approved_pharmacy) {
      setCapsule_changes_tracker(data.history);
    }
  };

  const onOpenPAFValidations = (data) => {
    const normalized = normalizePafPrescription(data);

    if (isPrescriber || isConnectedNurse) {
      // Pharmacies get full data via setOpenedItem in the path below. Prescribers skip that
      // path, but ViewPAF still reads from detailsPAF (detailsSelectedPAF), so we must store
      // the prescription payload when it is fetched or opened.
      setOpenedItem({ ...normalized });
      setUnchagedItems({ ...normalized });
      return;
    }

    validateOutpatientDispense(normalized);
    handleIsPharmaApproved(normalized);

    Object.assign(normalized, {
      confirmation: normalized.confirmation_risk_prescriber,
    });

    let infoUser = {
      ...normalized,
    };
    Object.assign(infoUser.patient, {
      treatingHospital: infoUser.patient.hospital_name,
    });

    setUserInfo(infoUser.patient);
    delete infoUser.patient;

    setOpenedItem({
      ...normalized,
      // Add default value for checkbox for Approve/Reject PAF to know the initial value
      confirmation: normalized.confirmation_risk_patient,
    });

    setUnchagedItems({ ...normalized });

    validateAdditionalOptions(normalized);
  };

  const getDataAboutCurrentForm = async (id) => {
    const item = detailsPAF.find((element) => element.id === id);

    if (item) {
      setActivateTab(id);
      validateAdditionalOptions(item);
      onOpenPAFValidations(item);
      return item;
    } else {
      const res = await Axios.get(APIVariables.PRESCRIPTIONS + id).catch(
        (error) => {
          handleCatch(error);
        }
      );

      if (res.status === 200) {
        if (!isConnectedNurse) {
          getHCP(res.data.product_id);
        }
        setActivateTab(id);
        validateAdditionalOptions(res?.data);
        setCapsule_changes_tracker(res.data.history);

        onOpenPAFValidations(res?.data);
      }
    }
  };

  useEffect(() => {
    if (!latestPafId || !items.length) return;
    getDataAboutCurrentForm(latestPafId);
    if (isPharmacy || isOutpatient || isHomecare) {
      if (pafID && String(pafID) !== String(latestPafId)) {
        navigate(
          `${App.PRESCRIPTION_AUTHORISATION_FORM_DEFAULT}${patientID}/${latestPafId}`,
          { replace: true, state: location.state }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestPafId, items.length, patientID]);

  const detailsSelectedPAF = (pafID) => {
    return detailsPAF.find((element) => element.id === pafID);
  };

  const getNumberOfCycles = async () => {
    if (!isPrescriber && !isConnectedNurse && !!pafID) {
      await Axios.get(APIVariables.PRESCRIPTIONS_ACCEPT + pafID)
        .then(async (res) => {
          if (res?.status === 200) {
            const dropDownValues = res?.data?.n_cycles;
            const rename = await dropDownValues;

            setNumberOfCylesValue(rename);
          }
        })
        .catch(function (error) {
          handleCatch(error);

          const errors = error?.response?.data;
          if (typeof errors === "string") {
            setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
          } else {
            const errorsArray = [];
            if (!!errors) {
              Object.keys(errors).forEach((key) => {
                if (isObject(errors[key])) {
                  Object.keys(errors[key]).forEach((secondKey) => {
                    errorsArray.push(key + " - " + errors[key][secondKey][0]);
                  });
                } else {
                  errorsArray.push(key + " - " + errors[key]);
                }
              });
              setErrorMessage(errorsArray);
            } else {
              setErrorMessage([error.message]);
            }
          }
        });
    }
  };

  const validateAdditionalOptions = (data) => {
    const validation = {
      capsuleStrength: !!data.strengths.length !== 0,
      capsulePerCycle: !!data.strengths.length !== 0,
      numberOfCycles: !(data.n_cycles === undefined || data.n_cycles === null),
    };

    const aditionalOptionsCopy = { ...aditionalOptions, ...validation };

    setAditionalOptions(aditionalOptionsCopy);
  };

  // Fetches the patient’s prescription list (items + latest/history IDs).
  const getPAF = async () => {
    setIsFormApproved(null);
    if (
      isPrescriber ||
      isPharmacy ||
      isConnectedNurse ||
      isOutpatient ||
      isHomecare
    ) {
      const res = await Axios.get(
        APIVariables.PATIENTS_DETAILS + patientID
      ).catch(function (error) {
        handleCatch(error);
        setIsLoading(false);
      });

      if (res?.status === 200) {
        let infoUser = { ...res.data };
        delete infoUser.prescriptions;
        setUserInfo(infoUser);
        setItems(res.data.prescriptions);

        setIsLoading(false);
      }
    }
  };

  function closeForm() {
    if (isPharmacy) {
      if (
        getOpenedItem().status === "Approved" ||
        getOpenedItem().status === "Rejected" ||
        getOpenedItem().status === "Dispensed"
      ) {
        navigate(App.VIEW_PATIENTS);
      } else {
        setIsOpenQuit(true);
      }
    } else {
      navigate(App.VIEW_PATIENTS);
    }
  }

  function closeModalQuit() {
    setIsOpenQuit(false);
  }

  function quitModal() {
    navigate(App.VIEW_PATIENTS);
  }

  function closeModalChangeValues() {
    setIChangeValuesModal((prevState) => ({
      ...prevState,
      isOpen: false,
      title: "",
      description: "",
      typeOfError: "",
    }));
  }

  function closemodalNonCompliantPAF() {
    setModalNonCompliantPAF((prevState) => ({
      ...prevState,
      isOpen: false,
      description: [],
      screen: 1,
    }));
  }

  function closemodalNonCompliantPAFWarning() {
    setModalNonCompliantPAFWarning((prevState) => ({
      ...prevState,
      isOpen: false,
      title: "",
      description: [],
      warningType: null,
    }));
  }

  function continueButton(typeOfError) {
    if (typeOfError === "capsule_strength") {
      setConfirmChangesCapsStrength(true);
    }

    if (typeOfError === "capsules_per_cycle") {
      setConfirmChangesCapsPerCycle(true);
    }

    if (typeOfError === "confirmWasChangedPregancyTest") {
      setConfirmWasChangedPregancyTest(true);
    }

    if (typeOfError === "confirmWasApproved") {
      setConfirmWasApproved(true);
    }

    if (typeOfError === "confirmWasChangedNumberOfCycles") {
      setConfirmWasChangedNumberOfCyles(true);
    }

    if (typeOfError === "confirmWasChangedWeeksPerCycle") {
      setConfirmWasChangedWeeksPerCycle(true);
    }

    if (typeOfError === "confirmWasPregancyTestIsNotValid") {
      setConfirmWasPregancyTestIsNotValid(true);
    }

    if (typeOfError === "confirmWasDispensed7DaysAfter") {
      setConfirmWasDispensed7DaysAfter(true);
    }

    closeModalChangeValues();
  }

  const handleChangeValidPassword = (event) => {
    setValidPassword(event.target.value);
  };

  const continueButtonNonComplainPAFWarning = (modalState) => {
    const title =
      typeof modalState === "string" ? modalState : modalState?.title;
    const warningType =
      typeof modalState === "object" && modalState
        ? modalState.warningType
        : null;

    if (title === errorMessages.OUTDATED_PREGANACY_TEST_TITLE) {
      setConfirmWasPregancyTestIsNotValid(true);
    }

    if (title === errorMessages.PREGNANACY_TEST_DATE_INCOMPLETE_TITLE) {
      setConfirmPregnancyTestDateIncompleteModal(false);
    }

    if (title === errorMessages.INDICATION_INCOMPLETE_TITLE) {
      setConfirmIndicationIncompleteModal(false);
    }

    if (title === errorMessages.MAXIM_NUMBER_OF_CYLES_EXCEEDED_TITLE) {
      setConfirmNumberOfCylesIsNotValidWarningModal(false);
    }

    if (title === "Maximum number of weeks exceeded") {
      setConfirmNumberOfWeeksIsNotValidAgainWarningModal(false);
    }

    if (warningType === errorMessages.OUTSIDE_PERMITTED_QUANTITY_WARNING) {
      setConfirmCapsulesPerWeeksIsNotValidWarningModal(false);
    }

    if (title === errorMessages.CONFIRMATION_NOT_PROVIDED_TITLE) {
      setConfirmCheckboxIsNotValidWarningModal(false);
    }

    closemodalNonCompliantPAFWarning();
  };

  const continueButtonNonComplainPAF = async (screen) => {
    setErrorMessage("");
    setErrorMessageFromServer("");

    if (screen === 1) {
      setModalNonCompliantPAF((prevState) => ({
        ...prevState,
        description: [],
        screen: 2,
      }));
    }

    if (screen === 2) {
      const res = await Axios.post(APIVariables.USERS_PASSWORD_VALIDATE, {
        password: validPassword,
      }).catch(function (error) {
        handleCatch(error);

        const errors = error?.response?.data;
        if (typeof errors === "string") {
          setErrorMessageFromServer([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          if (!!errors) {
            Object.keys(errors).forEach((key) => {
              if (isObject(errors[key])) {
                Object.keys(errors[key]).forEach((secondKey) => {
                  // errorsArray.push(key + " - " + errors[key][secondKey][0]);
                  errorsArray.push(errors[key][secondKey][0]);
                });
              } else {
                // errorsArray.push(key + " - " + errors[key]);
                errorsArray.push(errors[key]);
              }
            });
            setErrorMessageFromServer(errorsArray);
          } else {
            setErrorMessageFromServer([error.message]);
          }
        }
      });

      if (res?.status === 200) {
        if (isOutpatient || isHomecare) {
          closemodalNonCompliantPAF();
          setContinuDispenseOverduePAF((prev) => ({
            ...prev,
            isValidNonComplaintHomecareAndOutPatient: true,
          }));
        } else {
          setModalNonCompliantPAF((prevState) => ({
            ...prevState,
            description: [],
            screen: 3,
          }));
        }
      }
    }
    if (screen === 3) {
      if (isApproveAndDispense) {
        setShowMahSelection(true);
      } else {
        setShowNominateDispenser(true);
      }
      closemodalNonCompliantPAF();
    }
  };

  const completeNonComplaintPAF = (payload) => {
    setIsFormApproved(true);
    closemodalNonCompliantPAF();
    sendPafConclusion(true, payload);
  };

  const displayWeeks = openedPAF?.schedule_display_type === "weeks";

  const checkIfNumberOfCyclesExceeded = (form) => {
    const getNumberOfCyles = openedPAF?.new_capsule_cycles?.id
      ? openedPAF?.new_capsule_cycles?.id
      : +form?.n_cycles?.split(" ")[0];

    const typeOfFormVar = typeOfForm();
    if (
      typeOfFormVar === typeOfPAF.MALE ||
      typeOfFormVar === typeOfPAF.WOMANOFNCP
    ) {
      if (
        getNumberOfCyles >
        (openedPAF?.schedule_display_type !== "weeks" && 3)
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      if (
        getNumberOfCyles >
        (openedPAF?.schedule_display_type !== "weeks" ? 1 : 4)
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const doDispensing = async (dispensingForm) => {
    await Axios.post(
      APIVariables.PRESCRIPTIONS + pafID + APIVariables.DISPENSE,
      dispensingForm
    )
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          navigate(App.VIEW_PATIENTS);
          setForm({ ...form, ...data });
        }
      })
      .catch(function (error) {
        handleCatch(error);
      });

    updateNotifications();
  };

  async function approveForm() {
    if (
      !isValidCapsules ||
      !isValidCycles ||
      !isValidStrengths ||
      !isValidWeeksPerCycle
    ) {
      setAditionalOptionsState({ ...aditionalOptionsState, showErrors: true });
      window.scrollTo(0, document.body.scrollHeight);
      return;
    }

    const patientType = getOpenedItem().patient.patient_type;
    const openedPaf = getOpenedItem();
    const unitLabelSingularLc = capitalizeFirstLetter(
      openedPaf?.unit_label || "capsule"
    ).toLowerCase();
    const unitLabelPluralLc = pluralizeUnit(
      capitalizeFirstLetter(openedPaf?.unit_label || "capsule")
    ).toLowerCase();

    const isPregancyTestOutdated = getOpenedItem().pregnancy_test
      ? isDispensingMoreThan3DaysBetween2Dates(
        getOpenedItem()?.pfa_issued,
        getOpenedItem()?.pregnancy_test
      )
      : false;
    const isPreganacyTestIncomplete =
      (getOpenedItem().pregnancy_test !== true &&
        getOpenedItem().pregnancy_test === undefined) ||
      getOpenedItem().pregnancy_test === null;

    const isNumberOfCylesExceeded = checkIfNumberOfCyclesExceeded(
      getOpenedItem()
    );
    const checkIfNumberOfWeeksExceeded = (form) => {
      const typeOfFormVar = !!form?.type ? form?.type : form?.patient?.type;

      const isWOMANOFCP = typeOfFormVar === typeOfPAF.WOMANOFCP;
      const isMALE = typeOfFormVar === typeOfPAF.MALE;
      const isWOMANOFNCP = typeOfFormVar === typeOfPAF.WOMANOFNCP;

      const maxWeeksWCBP = form?.max_weeks_wcbp || form?.max_weeks_wcbp;
      const maxWeeksMOrWNCBP =
        form?.max_weeks_m_or_wncbp || form?.max_weeks_m_or_wncbp;

      const nrCycles = !!form?.new_capsule_cycles?.id
        ? form?.new_capsule_cycles?.id
        : !!form?.n_weeks_other
          ? form?.n_weeks_other
          : !!form?.other
            ? parseFloat(form?.other)
            : parseFloat(form?.n_weeks);

      return isWOMANOFCP
        ? maxWeeksWCBP && maxWeeksWCBP > 0
          ? nrCycles > maxWeeksWCBP
          : false
        : isMALE || isWOMANOFNCP
          ? maxWeeksMOrWNCBP && maxWeeksMOrWNCBP > 0
            ? nrCycles > maxWeeksMOrWNCBP
            : false
          : false;
    };

    const isChangedWeeksPerCycle = () => {
      const checkNewWeeksPerCycle =
        (openedPaf?.new_weeks_per_cycle?.id === 0
          ? openedPaf?.new_weeks_per_cycle_other
          : openedPaf?.new_weeks_per_cycle?.id) ||
        (openedPaf?.new_weeks_per_cycle?.id === 0
          ? openedPaf?.new_weeks_per_cycle_other
          : openedPaf?.new_weeks_per_cycle?.id);
      if (checkNewWeeksPerCycle) {
        return true;
      } else {
        return false;
      }
    };

    const checkIfTotalSupplyWeeksExceeded = (form) => {
      const typeOfFormVar = !!form?.type ? form?.type : form?.patient?.type;

      const isWOMANOFCP = typeOfFormVar === typeOfPAF.WOMANOFCP;
      const isMALE = typeOfFormVar === typeOfPAF.MALE;
      const isWOMANOFNCP = typeOfFormVar === typeOfPAF.WOMANOFNCP;

      const maxWeeksWCBP = form?.max_weeks_wcbp || form?.max_weeks_wcbp;
      const maxWeeksMOrWNCBP =
        form?.max_weeks_m_or_wncbp || form?.max_weeks_m_or_wncbp;

      return isWOMANOFCP
        ? maxWeeksWCBP && maxWeeksWCBP > 0
          ? form?.total_weeks_supply > maxWeeksWCBP
          : false
        : isMALE || isWOMANOFNCP
          ? maxWeeksMOrWNCBP && maxWeeksMOrWNCBP > 0
            ? form?.total_weeks_supply > maxWeeksMOrWNCBP
            : false
          : false;
    };

    const checkIfTotalCapsulesExceeded = (form) => {
      const typeOfFormVar = !!form?.type ? form?.type : form?.patient?.type;

      if (form.schedule_display_type === "weeks") {
        const totalCapsules = !!form?.new_totalCapsules
          ? form?.new_totalCapsules
          : form?.total_capsules;

        return typeOfFormVar === typeOfPAF.WOMANOFCP
          ? form?.max_capsules_wcbp > 0 &&
          totalCapsules > form?.max_capsules_wcbp
          : form?.max_capsules_m_or_wncbp > 0
            ? totalCapsules > form?.max_capsules_m_or_wncbp
            : false;
      }
    };

    const isIndicationIncomplete =
      getOpenedItem()?.indication === undefined ||
      getOpenedItem()?.indication === null;

    if (confirmChangesCapsStrength !== true) {
      // API often omits new_* fields when unchanged (undefined) instead of null; strict === null
      // would treat that as a pending amendment and open this modal incorrectly.
      let areCapsStrengthChanged = openedPaf?.strengths?.every(
        (element) => element.new_strength_id == null
      );

      if (!areCapsStrengthChanged) {
        setConfirmChangesCapsStrength(false);
        setIChangeValuesModal((prevState) => ({
          ...prevState,
          isOpen: true,
          title: `Amend the ${openedPaf?.schedule_display_type === "weeks_per_cycle"
            ? "dosage"
            : unitLabelSingularLc
            } strength provided for this patient`,
          description: `I acknowledge that the ${openedPaf?.schedule_display_type === "weeks_per_cycle"
            ? "dosage"
            : unitLabelSingularLc
            } strength inputted originally was inaccurate. I wish to amend the ${openedPaf?.schedule_display_type === "weeks_per_cycle"
              ? "dosage strength"
              : `${unitLabelSingularLc} strength`
            } provided based on consultation with the prescriber/patient.`,
          typeOfError: "capsule_strength",
        }));
        return;
      }
    }

    if (confirmChangesCapsPerCycle !== true) {
      let areCapsCyclesChanged = openedPaf?.strengths?.every(
        (element) => element.new_capsules == null
      );

      if (!areCapsCyclesChanged) {
        const scheduleLabel =
          openedPaf?.schedule_display_type === "weeks_per_cycle"
            ? "cycles"
            : openedPaf?.schedule_display_type;
        setConfirmChangesCapsPerCycle(false);
        setIChangeValuesModal((prevState) => ({
          ...prevState,
          isOpen: true,
          title: `Amend the ${unitLabelPluralLc} per ${scheduleLabel} provided for this patient`,
          description: `I acknowledge that the ${unitLabelPluralLc} per ${scheduleLabel} inputted originally was inaccurate. I wish to amend the ${unitLabelPluralLc} per ${scheduleLabel} provided based on consultation with the prescriber/patient.`,
          typeOfError: "capsules_per_cycle",
        }));
        return;
      }
    }

    const isChangedNrCycles = () => {
      const checkNewNCycles =
        openedPaf?.new_capsule_cycles?.label === "Other (Please State)"
          ? openedPaf?.n_weeks_other
          : openedPaf?.new_capsule_cycles?.id;

      if (checkNewNCycles) {
        return true;
      } else {
        return false;
      }
    };

    if (isChangedNrCycles() && confirmWasChangedNumberOfCycles !== true) {
      setConfirmWasChangedNumberOfCyles(false);
      setIChangeValuesModal((prevState) => ({
        ...prevState,
        isOpen: true,
        title: `Amend the number of ${openedPaf?.schedule_display_type === "weeks_per_cycle"
          ? "cycles"
          : openedPaf?.schedule_display_type
          } provided for this patient`,
        description: `I acknowledge that the number of ${openedPaf?.schedule_display_type === "weeks_per_cycle"
          ? "cycles"
          : openedPaf?.schedule_display_type
          } inputted originally was inaccurate. I wish to amend the number of ${openedPaf?.schedule_display_type === "weeks_per_cycle"
            ? "cycles"
            : openedPaf?.schedule_display_type
          } provided based on consultation with the prescriber/patient.`,
        typeOfError: "confirmWasChangedNumberOfCycles",
      }));
      return;
    }

    if (
      openedPaf?.schedule_display_type === "weeks_per_cycle" &&
      isChangedWeeksPerCycle() &&
      confirmWasChangedWeeksPerCycle !== true
    ) {
      setConfirmWasChangedWeeksPerCycle(false);
      setIChangeValuesModal((prevState) => ({
        ...prevState,
        isOpen: true,
        title: `Amend the weeks per cycles provided for this patient`,
        description: `I acknowledge that the weeks per cycles inputted originally was inaccurate. I wish to amend the weeks per cycles provided based on consultation with the prescriber/patient.`,
        typeOfError: "confirmWasChangedWeeksPerCycle",
      }));
      return;
    }

    if (patientType === "Woman of Childbearing Potential") {
      if (isPreganacyTestIncomplete) {
        if (confirmPregnancyTestDateIncompleteModal !== false) {
          setConfirmPregnancyTestDateIncompleteModal(true);

          setModalNonCompliantPAFWarning((prevState) => ({
            ...prevState,
            isOpen: true,
            warningType: null,
            title: errorMessages.PREGNANACY_TEST_DATE_INCOMPLETE_TITLE,
            description: (
              <BrochureWithText
                type="NEED_VALID_NEGATIVE_TEST_INCOMPLETE"
                hcp={hcp}
              />
            ),
          }));
          return;
        } else {
          setConfirmPregnancyTestDateIncompleteModal(false);
        }
      } else {
        setConfirmPregnancyTestDateIncompleteModal(null);
      }
    }

    if (
      getOpenedItem()?.confirmIndicationIncompleteModal !== true &&
      isIndicationIncomplete
    ) {
      if (confirmIndicationIncompleteModal !== false) {
        setConfirmIndicationIncompleteModal(true);

        setModalNonCompliantPAFWarning((prevState) => ({
          ...prevState,
          isOpen: true,
          warningType: null,
          title: errorMessages.INDICATION_INCOMPLETE_TITLE,
          description: (
            <BrochureWithText
              type={"CONTINUE_INDICATION_INCOMPLETE"}
              hcp={hcp}
            />
          ),
        }));
        return;
      } else {
        setConfirmIndicationIncompleteModal(false);
      }
    } else {
      setConfirmIndicationIncompleteModal(false);
    }

    if (isNumberOfCylesExceeded && !displayWeeks) {
      if (confirmNumberOfCylesIsNotValidWarningModal !== false) {
        setConfirmNumberOfCylesIsNotValidWarningModal(true);

        setModalNonCompliantPAFWarning((prevState) => ({
          ...prevState,
          isOpen: true,
          warningType: null,
          title: errorMessages.MAXIM_NUMBER_OF_CYLES_EXCEEDED_TITLE,
          description:
            patientType === "Male" ? (
              <BrochureWithText type="CYCLES_EXCEEDED_MALE" hcp={hcp} />
            ) : patientType === "Woman of Childbearing Potential" ? (
              <BrochureWithText type={"CYCLES_EXCEEDED_WCBP"} hcp={hcp} />
            ) : (
              <BrochureWithText type="CYCLES_EXCEEDED_WOMANOFNCP" hcp={hcp} />
            ), // to be changed
        }));
        return;
      } else {
        setConfirmNumberOfCylesIsNotValidWarningModal(false);
      }
    } else {
      setConfirmNumberOfCylesIsNotValidWarningModal(false);
    }

    if (displayWeeks && checkIfNumberOfWeeksExceeded(openedPAF)) {
      if (confirmNumberOfWeeksIsNotValidAgainWarningModal !== false) {
        setConfirmNumberOfWeeksIsNotValidAgainWarningModal(true);

        setModalNonCompliantPAFWarning((prevState) => ({
          ...prevState,
          isOpen: true,
          warningType: null,
          title: "Maximum number of weeks exceeded",
          description: (
            <BrochureWithText
              type={"WEEKS_EXCEEDED"}
              typeOfPAF={typeOfPAF}
              registerPatientType={typeOfForm}
              maxCapsulesOrWeeks={openedPAF}
              hcp={hcp}
            />
          ),
        }));
        return;
      } else {
        setConfirmNumberOfWeeksIsNotValidAgainWarningModal(false);
      }
    } else {
      setConfirmNumberOfWeeksIsNotValidAgainWarningModal(false);
    }

    if (
      openedPAF?.schedule_display_type === "weeks_per_cycle" &&
      checkIfTotalSupplyWeeksExceeded(openedPAF)
    ) {
      if (confirmNumberOfWeeksIsNotValidAgainWarningModal !== false) {
        setConfirmNumberOfWeeksIsNotValidAgainWarningModal(true);

        setModalNonCompliantPAFWarning((prevState) => ({
          ...prevState,
          isOpen: true,
          warningType: null,
          title: "Maximum number of weeks exceeded",
          description: (
            <BrochureWithText
              type={"WEEKS_EXCEEDED"}
              typeOfPAF={typeOfPAF}
              registerPatientType={typeOfForm}
              maxCapsulesOrWeeks={openedPAF}
              hcp={hcp}
            />
          ),
        }));
        return;
      } else {
        setConfirmNumberOfWeeksIsNotValidAgainWarningModal(false);
      }
    } else {
      setConfirmNumberOfWeeksIsNotValidAgainWarningModal(false);
    }

    if (displayWeeks && checkIfTotalCapsulesExceeded(openedPAF)) {
      if (confirmCapsulesPerWeeksIsNotValidWarningModal !== false) {
        setConfirmCapsulesPerWeeksIsNotValidWarningModal(true);

        setModalNonCompliantPAFWarning((prevState) => ({
          ...prevState,
          isOpen: true,
          warningType: errorMessages.OUTSIDE_PERMITTED_QUANTITY_WARNING,
          title: formatOutsidePermittedQuantityTitle(openedPAF?.unit_label),
          description: (
            <BrochureWithText
              type={"CAPSULES_EXCEEDED"}
              typeOfPAF={typeOfPAF}
              registerPatientType={openedPAF?.patient?.type}
              maxCapsulesOrWeeks={openedPAF}
              hcp={hcp}
              unitLabel={openedPAF?.unit_label}
            />
          ),
        }));
        return;
      } else {
        setConfirmCapsulesPerWeeksIsNotValidWarningModal(false);
      }
    } else {
      setConfirmCapsulesPerWeeksIsNotValidWarningModal(false);
    }

    if (patientType !== "Woman of Non-Childbearing Potential") {
      if (allConfirmed !== true) {
        if (confirmCheckboxIsNotValidWarningModal !== false) {
          setConfirmCheckboxIsNotValidWarningModal(true);

          setModalNonCompliantPAFWarning((prevState) => ({
            ...prevState,
            isOpen: true,
            warningType: null,
            title: errorMessages.CONFIRMATION_NOT_PROVIDED_TITLE,
            description: (
              <BrochureWithText
                type={"CONFIRMATION_NOT_PROVIDED_DESCRIPTION"}
                hcp={hcp}
                confirmationRiskLength={
                  getOpenedItem()?.prescription_confirmation_risks?.length
                }
              />
            ),
          }));
          return;
        } else {
          setConfirmCheckboxIsNotValidWarningModal(false);
        }
      } else {
        setConfirmCheckboxIsNotValidWarningModal(false);
      }
    }

    setIsOtherNumberOfCycledValid(false);
    // IF OTHER OPTION IS SELECTED FROM NUMBER OF CYCLES TO OTHER AND OTHER IS VALID
    if (
      getOpenedItem().n_cycles !== unchagedItems.n_cycles ||
      getOpenedItem().n_cycles_other !== unchagedItems.n_cycles_other
    ) {
      if (getOpenedItem().n_cycles === "Other (please state)") {
        if (+getOpenedItem().n_cycles_other !== undefined) {
          setIsOtherNumberOfCycledValid(false);
        } else {
          setIsOtherNumberOfCycledValid(true);
        }
      } else {
        setIsOtherNumberOfCycledValid(false);
      }
    }

    // CHECK IF LAST NEGATIVE PREGANCY TEST WAS CHANGED ONLY FOR Woman of Childbearing Potential
    if (patientType === "Woman of Childbearing Potential") {
      if (
        getOpenedItem().pregnancy_test !== unchagedItems.pregnancy_test &&
        confirmWasChangedPregancyTest !== true
      ) {
        setConfirmWasChangedPregancyTest(false);
        setIChangeValuesModal((prevState) => ({
          ...prevState,
          isOpen: true,
          title: "Amend date of last negative pregnancy test",
          description:
            "I acknowledge that the date of the last negative pregnancy test that was originally inputted was outside the permitted date range. I wish to amend this date based on consultation with the prescriber/patient.",
          typeOfError: "confirmWasChangedPregancyTest",
        }));
        return;
      }
    }

    // CHECK IF LAST NEGATIVE PREGANCY TEST WAS CHANGED ONLY FOR Woman of Childbearing Potential and it is not valid
    if (patientType === "Woman of Childbearing Potential") {
      if (
        (getOpenedItem().pregnancy_test !== unchagedItems.pregnancy_test ||
          getOpenedItem().pregnancy_test !== null) &&
        isPregancyTestOutdated &&
        confirmWasPregancyTestIsNotValid !== true
      ) {
        setConfirmWasPregancyTestIsNotValid(false);
        setIChangeValuesModal((prevState) => ({
          ...prevState,
          isOpen: true,
          title: errorMessages.OUTDATED_PREGANACY_TEST_TITLE,
          description: (
            <BrochureWithText type={"NEED_VALID_NEGATIVE_TEST"} hcp={hcp} />
          ),
          typeOfError: "confirmWasPregancyTestIsNotValid",
        }));
        return;
      }
    }

    // CHECK IF CHECKBOX WAS CHANGED
    if (
      getOpenedItem()?.prescription_confirmation_risks !==
      unchagedItems.prescription_confirmation_risks &&
      confirmWasApproved !== true
    ) {
      setConfirmWasApproved(false);
      setIChangeValuesModal((prevState) => ({
        ...prevState,
        isOpen: true,
        title: "Confirm that the patient has been counselled",
        description:
          "I acknowledge that confirmation of patient counselling was not originally provided. I wish to amend this based on consultation with the prescriber/patient.",
        typeOfError: "confirmWasApproved",
      }));
      return;
    }

    const prescribedOnForm = form?.pfa_issued || getOpenedItem()?.pfa_issued;

    const isDispesngingMoreThan7Days = isDispensingMoreThan7DaysBetween2Dates(
      prescribedOnForm,
      new Date()
    );

    // CHECK IF PAF IS GREATER THAN 7 DAYS (PHARMACY VIEW AND WCBP PATIENT TYPE ONLY)
    if (patientType === "Woman of Childbearing Potential") {
      if (
        confirmWasDispensed7DaysAfter !== true &&
        isDispesngingMoreThan7Days
      ) {
        setConfirmWasDispensed7DaysAfter(false);
        setIChangeValuesModal((prevState) => ({
          ...prevState,
          isOpen: true,
          title: errorMessages.DISPENSING_MORE_THAN_7_DAYS_TITLE,
          description: errorMessages.DISPENSING_MORE_THAN_7_DAYS_DESCRIPTION,
          typeOfError: "confirmWasDispensed7DaysAfter",
        }));
        return;
      }
    }

    // CHECK IF PAF IS NON-COMPLIANT
    const errors = [];

    // IS PREGNANCY TEST OUTDATED

    if (patientType === "Woman of Childbearing Potential") {
      if (
        isPregancyTestOutdated &&
        (form.pregnancy_test !== undefined || form.pregnancy_test !== null)
      ) {
        errors.push("Pregnancy test date was outside the permitted date range");
      }
      if (isPreganacyTestIncomplete) {
        errors.push("Pregnancy test incomplete");
      }
    }

    // IS INDICATION INCOMPLETE
    if (isIndicationIncomplete) {
      errors.push("Indication incomplete");
    }

    // IS NUMBER OF CYCLES EXCEEDED (non–weeks-based schedules, incl. weeks per cycle)
    if (!displayWeeks && checkIfNumberOfCyclesExceeded(getOpenedItem())) {
      errors.push("Maximum number of cycles exceeded for this patient");
    }

    if (displayWeeks && checkIfNumberOfWeeksExceeded(openedPAF)) {
      errors.push("Maximum number of weeks exceeded for this patient");
    }
    if (
      openedPAF?.schedule_display_type === "weeks_per_cycle" &&
      checkIfTotalSupplyWeeksExceeded(openedPAF)
    ) {
      errors.push("Maximum number of weeks exceeded for this patient");
    }

    if (displayWeeks && checkIfTotalCapsulesExceeded(openedPAF)) {
      errors.push(
        formatOutsidePermittedQuantityTitle(openedPAF?.unit_label)
      );
    }
    // VALIDATE DISPESING MORE THAN 7 DAYS

    if (patientType === "Woman of Childbearing Potential") {
      if (isDispesngingMoreThan7Days) {
        errors.push(errorMessages.DISPENSING_MORE_THAN_7_DAYS_TITLE);
      }
    }

    // VALIDATE PRESCRIBED ON
    const typeOfFormVar = typeOfForm();
    if (allConfirmed === false && typeOfFormVar !== typeOfPAF.WOMANOFNCP) {
      errors.push("Confirm that the patient has been counselled");
    }
    let isValidPayload = await checkApproveForm();

    if (errors.length) {
      setModalNonCompliantPAF((prevState) => ({
        ...prevState,
        isOpen: true,
        description: errors,
        screen: 1,
      }));
    } else {
      if (isValidPayload) {
        setStatePAFModal(true);
        setMessage((prevState) => ({
          ...prevState,
          state: 1,
          type: "valid",
          title: "Please confirm your approval",
          // description: `
          //   <div>I have read and understood the <a href="/" target="_blank" rel="noreferrer">Lenalidomide Healthcare Professional Brochure</a>. I confirm the information provided on this PAF is accurate, complete and in accordance with the requirements of the pregnancy prevention programme for Lenalidomide. </div>
          //   ${
          //     patientType === "Male"
          //       ? '<div class="mt-4">For male patients; (1) The patient has been counselled/reminded about teratogenic risk and the requirement to use a condom if sexually active with a pregnant woman or a woman of childbearing potential not using effective contraception. (2) The supply of treatment is no more than 12 weeks.</div>'
          //       : patientType === "Woman of Childbearing Potential"
          //       ? '<div class="mt-4">For a woman of childbearing potential (WCBP); (1) The WCBP has been counselled/reminded about teratogenic risk and has been on at least one effective method of contraception for at least 4 weeks. (2) The WCBP has had a negative pregnancy test within the 3 days before the prescription date. (3) The dispensing of lenalidomide is within 7 days of the prescription date. (4) The supply of treatment is no more than 4 weeks.</div>'
          //       : '<div class="mt-4">For women of non-childbearing potential the supply of treatment is no more than 12 weeks.</div>'
          //   }
          // `,
          description: (
            <BrochureWithText
              type={"PLEASE_CONFIRM_APPROVAL"}
              patientType={patientType}
              hcp={hcp}
            />
          ),
          buttonText: "Approve",
        }));
      }
    }
  }

  // View Patients preview: location.state runs Approve / Approve & dispense / Reject once after load.
  useEffect(() => {
    const locationState = location.state;
    const autoApprove = locationState?.fromViewPatientsAutoApprove === true;
    const autoReject = locationState?.fromViewPatientsAutoReject === true;
    if (!autoApprove && !autoReject) return;
    if (isLoading) return;
    if (!openedPAF?.id || String(openedPAF.id) !== String(pafID)) return;

    navigate(
      { pathname: location.pathname, search: location.search || "" },
      { replace: true, state: {} }
    );

    if (autoReject) {
      queueMicrotask(() => {
        rejectForm();
      });
      return;
    }

    const approveAndDispense = locationState.approveAndDispense === true;
    if (approveAndDispense) {
      setIsApproveAndDispense(true);
      setShowNominateDispenser(false);
    } else {
      setIsApproveAndDispense(false);
      setShowMahSelection(false);
    }
    queueMicrotask(() => {
      void approveForm();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run when data matches URL; approveForm/rejectForm closures
  }, [
    isLoading,
    openedPAF?.id,
    pafID,
    location.state,
    location.pathname,
    location.search,
    navigate,
  ]);

  const closeConfirmationModal = () => {
    setStatePAFModal(false);
    setMessage((prevState) => ({
      ...prevState,
      state: 1,
      type: "",
      title: "",
      description: "",
      buttonText: "",
    }));
  };

  function rejectForm() {
    setStatePAFModal(true);
    submitError();
  }

  function revertToApprover() {
    setStatePAFModal(true);
    setMessage((prevState) => ({
      ...prevState,
      state: 3,
      type: "revert",
      title: "State the Reason for Reverting to the Approver",
      description: "",
      buttonText: "Continue",
    }));
  }

  const submitError = () => {
    setMessage((prevState) => ({
      ...prevState,
      state: 2,
      type: "error",
      title: "State the Reason for Rejection",
      description: "",
      buttonText: "Reject",
    }));
  };

  const submitValidate = () => {
    if (isApproveAndDispense) {
      setShowMahSelection(true);
    } else {
      setShowNominateDispenser(true);
    }
    closeConfirmationModal();
    setCompliantPAF(true);
  };

  const updatedStrengths =
    openedPAF?.strengths?.map((currentObject) => {
      // Check if new_capsules_other is equal to capsules
      if (currentObject.new_capsules_other === currentObject.capsules) {
        return {
          ...currentObject,
          new_capsules_other: null,
          is_new_other_capsules: false,
          new_capsules: null,
        };
      }
      return currentObject;
    }) ?? [];

  const updatedN_cycles =
    !displayWeeks && !!openedPAF?.new_capsule_cycles?.id
      ? openedPAF?.new_capsule_cycles?.id
      : parseFloat(openedPAF?.n_cycles);

  const updatedN_weeks =
    displayWeeks && openedPAF?.new_capsule_cycles?.id
      ? openedPAF?.new_capsule_cycles?.id
      : isNaN(parseFloat(openedPAF?.n_weeks))
        ? 0
        : parseFloat(openedPAF?.n_weeks);

  const updateN_weeks_other = !!openedPAF?.n_weeks_other
    ? openedPAF?.n_weeks_other
    : null;

  const weeksPerCycleValue = openedPAF?.new_weeks_per_cycle
    ? openedPAF.new_weeks_per_cycle.id === 0
      ? openedPAF.new_weeks_per_cycle_other
      : openedPAF.new_weeks_per_cycle.id
    : openedPAF?.weeks_per_cycle;

  const prescriptionRisk = {};
  openedPAF?.prescription_confirmation_risks?.forEach((item) => {
    prescriptionRisk[item.confirmation_risk_id] =
      item.confirmation_risk_patient;
  });

  const dispensePAF = async (dispensingForm) => {
    const payloadToSend = {
      prescription_confirmation_risks: prescriptionRisk,
      pregnancy_test: getOpenedItem().pregnancy_test,
      strengths: updatedStrengths,
      n_cycles: updatedN_cycles,
      n_weeks: updatedN_weeks,
      n_weeks_other: updateN_weeks_other,
      weeks_per_cycle: weeksPerCycleValue,
      dispense: dispensingForm,
    };

    await Axios.post(
      APIVariables.PRESCRIPTIONS + pafID + APIVariables.APPROVE_AND_DISPENSE,
      payloadToSend
    )
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          navigate(App.VIEW_PATIENTS);
          setForm({ ...form, ...data });
          setIsOperationInProgress(false);
        }
      })
      .catch(function (error) {
        const errorDetail = error?.response?.data?.detail || "An error occurred";

        setErrorMessage(Array.isArray(errorDetail) ? errorDetail.join(", ") : errorDetail);
        setIsOperationInProgress(false);
        setShowMahSelection(false);
        setShowNominateDispenser(false);
        handleCatch(error);
      });
    updateNotifications();
  };

  //info payload is the nominate object
  const sendComplaintPAF = (payload) => {
    const isFormApprovedParam = true;
    setIsFormApproved(isFormApprovedParam);
    sendPafConclusion(isFormApprovedParam, payload);
  };

  const handleChangeSelect = (selectedElement, name) => {
    if (name === "reason_reverting") {
      setRevert((prevState) => ({
        ...prevState,
        reason_revert: selectedElement,
        reason_revert_input: "",
      }));
      setErrors({ ...errors, reason_revert: "" });
    }

    if (name === "reason_rejection") {
      setForm((prevState) => ({
        ...prevState,
        [name]: selectedElement,
        reason_rejection_input: "",
      }));
      setErrors({ ...errors, reason_rejection: "" });
    }
  };

  const handleChange = (name, value) => {
    if (name === "reason_revert_input") {
      setRevert({ ...revert, [name]: value });

      setErrors({ ...errors, reason_revert_input: "" });
      if (revert.is_revert_reason_other === true) {
        if (!value?.trim().length > 0) {
          setErrors({ ...errors, reason_revert_input: "Required" });
        }
      }
    }

    if (name === "reason_rejection_input") {
      setForm({ ...form, [name]: value });
      setErrors({ ...errors, reason_rejection_input: "" });
      if (form.is_rejection_reason_other === true) {
        if (!value?.trim().length > 0) {
          setErrors({ ...errors, reason_rejection_input: "Required" });
        }
      }
    }
  };

  const handleChangeSubmitedForm = (type, date, index) => {
    if (type === "capsule_strength") {
      const { oldStrength, e } = date;
      if (parseInt(oldStrength) === e?.value) {
        return;
      }
      setConfirmChangesCapsStrength(null);
    }

    if (type === "capsules_per_cycle") {
      const { e, capsule_cycle, other_caps } = date;
      const { value, label } = e;

      if (label?.toLowerCase().includes("other")) {
        if (
          getOpenedItem()[other_caps] &&
          getFirstNumberFromString(getOpenedItem()[other_caps]) === value
        ) {
          return;
        }
      } else {
        if (
          getOpenedItem()[capsule_cycle] &&
          getFirstNumberFromString(getOpenedItem()[capsule_cycle]) === value
        ) {
          return;
        }
      }

      setConfirmChangesCapsPerCycle(null);
    }

    if (type === "pregnancy_test") {
      setConfirmWasChangedPregancyTest(null);
      setConfirmWasPregancyTestIsNotValid(null);
      const formatData = formatDateDash(date);
      const detailsPAFIndex = detailsPAF.findIndex(
        (element) => element.id === activatedItem
      );

      let setNewItems = { ...detailsPAF[detailsPAFIndex] };

      setNewItems.pregnancy_test = formatData;

      const detailsPAFCopy = [...detailsPAF];

      detailsPAFCopy[detailsPAFIndex] = setNewItems;

      setDetailsPAF(detailsPAFCopy);
    }

    if (type === "number_cycles") {
      setConfirmWasChangedNumberOfCyles(null);
      setConfirmNumberOfCylesIsNotValidWarningModal(null);

      const detailsPAFIndex = detailsPAF.findIndex(
        (element) => element.id === activatedItem
      );

      const new_number_cles_value = date.label;
      let setNewItems = { ...detailsPAF[detailsPAFIndex] };
      setNewItems.n_cycles = new_number_cles_value;

      if (new_number_cles_value !== "Other (please state)") {
        setNewItems.n_cycles_other = 0;
      }

      const detailsPAFCopy = [...detailsPAF];

      detailsPAFCopy[detailsPAFIndex] = setNewItems;

      setDetailsPAF(detailsPAFCopy);
      handleAditionalOptionsChange(true, "select", "numberOfCycles");
    }

    if (type === "confirmation") {
      setConfirmWasApproved(null);

      const detailsPAFIndex = detailsPAF.findIndex(
        (element) => element.id === activatedItem
      );

      let setNewItems = { ...detailsPAF[detailsPAFIndex] };

      const updatedConfirmationRisks =
        setNewItems.prescription_confirmation_risks.map((item, i) => {
          if (i === index) {
            return {
              ...item,
              confirmation_risk_patient: !item.confirmation_risk_patient,
              confirmation_checked_by_pharmacist:
                !item.confirmation_risk_patient,
            };
          }
          return item;
        });

      setNewItems = {
        ...setNewItems,
        prescription_confirmation_risks: updatedConfirmationRisks,
      };

      const detailsPAFCopy = [...detailsPAF];

      detailsPAFCopy[detailsPAFIndex] = setNewItems;

      setDetailsPAF(detailsPAFCopy);
    }
  };

  const validateBeforeSending = () => {
    let err = {};
    if (message.state === 2) {
      if (form.reason_rejection === "") {
        err = { ...errors, reason_rejection: "Required" };
      }
      if (form.reason_rejection.is_rejection_reason_other) {
        if (!form.reason_rejection_input?.trim().length > 0) {
          err = { ...errors, reason_rejection_input: "Required" };
        }
      }
    }

    if (message.state === 3) {
      if (revert.reason_revert === "") {
        err = { ...errors, reason_revert: "Required" };
      }
      if (revert.reason_revert.is_revert_reason_other) {
        if (!revert.reason_revert_input?.trim().length > 0) {
          err = { ...errors, reason_revert_input: "Required" };
        }
      }
    }

    setErrors(err);

    if (Object.keys(err).length === 0) {
      const isFormApprovedParam = false;
      setIsFormApproved(isFormApprovedParam);

      sendPafConclusion(isFormApprovedParam);
    }
  };

  const checkApproveForm = async () => {
    const payload = {
      strengths: openedPAF?.strengths ?? [],
      prescription_confirmation_risks: prescriptionRisk,
      pregnancy_test: getOpenedItem().pregnancy_test,
      n_cycles:
        !displayWeeks && openedPAF?.new_capsule_cycles?.id
          ? openedPAF?.new_capsule_cycles?.id
          : parseFloat(openedPAF?.n_cycles),

      n_weeks:
        displayWeeks && openedPAF?.new_capsule_cycles?.id
          ? openedPAF?.new_capsule_cycles?.id
          : isNaN(parseFloat(openedPAF?.n_weeks))
            ? 0
            : parseFloat(openedPAF?.n_weeks),
      n_weeks_other:
        openedPAF?.new_capsule_cycles?.id === 0 ||
          openedPAF?.n_weeks === 0 ||
          isNaN(parseFloat(openedPAF?.n_weeks))
          ? openedPAF?.n_weeks_other
          : 0,

      weeks_per_cycle: weeksPerCycleValue,
    };

    let res = await Axios.post(
      APIVariables.PRESCRIPTIONS_ACCEPT + pafID + "/validate",
      payload
    ).catch(function (error) {
      handleCatch(error);

      const errors = error?.response?.data;
      if (typeof errors === "string") {
        setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
      } else {
        const errorsArray = [];
        if (!!errors) {
          Object.keys(errors).forEach((key) => {
            if (isObject(errors[key])) {
              Object.keys(errors[key]).forEach((secondKey) => {
                errorsArray.push(key + " - " + errors[key][secondKey][0]);
              });
            } else {
              errorsArray.push(key + " - " + errors[key]);
            }
          });
          setErrorMessage(errorsArray);
        } else {
          setErrorMessage([error.message]);
        }
      }
    });
    if (res?.status !== 200) {
      return false;
    }
    return true;
  };

  const sendPafConclusion = async (isFormApprovedParam, payload) => {
    let res;
    if (isFormApprovedParam) {
      const payloadToSend = {
        strengths: updatedStrengths,
        prescription_confirmation_risks: prescriptionRisk,
        pregnancy_test: getOpenedItem().pregnancy_test,
        n_cycles: updatedN_cycles,
        n_weeks: updatedN_weeks,
        n_weeks_other: updateN_weeks_other,
        weeks_per_cycle: weeksPerCycleValue,
        nominate: payload,
      };

      res = await Axios.post(
        APIVariables.PRESCRIPTIONS + pafID + APIVariables.APPROVE_AND_NOMINATE,
        payloadToSend
      ).catch(function (error) {
        handleCatch(error);

        const errors = error?.response?.data;
        if (typeof errors === "string") {
          setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          if (!!errors) {
            Object.keys(errors).forEach((key) => {
              if (isObject(errors[key])) {
                Object.keys(errors[key]).forEach((secondKey) => {
                  errorsArray.push(key + " - " + errors[key][secondKey][0]);
                });
              } else {
                errorsArray.push(key + " - " + errors[key]);
              }
            });
            setErrorMessage(errorsArray);
          } else {
            setErrorMessage([error.message]);
          }
        }
      });

      if (res?.status === 200) {
        setForm({ ...form, ...res?.data });
        setIsFormApproved(true);
        closeConfirmationModal();
      }
    } else if (message.state === 2) {
      res = await Axios.put(APIVariables.PRESCRIPTIONS_REJECT + pafID, {
        rejection_reason: form.reason_rejection.value,
        rejection_reason_other: form.reason_rejection_input ?? null,
      }).catch(function (error) {
        handleCatch(error);

        const errors = error?.response?.data;
        if (typeof errors === "string") {
          setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          if (!!errors) {
            Object.keys(errors).forEach((key) => {
              if (isObject(errors[key])) {
                Object.keys(errors[key]).forEach((secondKey) => {
                  errorsArray.push(key + " - " + errors[key][secondKey][0]);
                });
              } else {
                errorsArray.push(key + " - " + errors[key]);
              }
            });
            setErrorMessage(errorsArray);
          } else {
            setErrorMessage([error.message]);
          }
        }
      });

      if (res?.status === 200) {
        // simple page refresh to reload updated PAF from server
        closeConfirmationModal();
        // full reload (forces data re-fetch)
        window.location.reload();
      }
    } else {
      res = await Axios.put(APIVariables.PRESCRIPTIONS_REVERT + pafID, {
        revert_reason: revert.reason_revert.value,
        revert_reason_other: revert.reason_revert_input ?? null,
      }).catch(function (error) {
        handleCatch(error);

        const errors = error?.response?.data;
        if (typeof errors === "string") {
          setErrorMessage([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          if (!!errors) {
            Object.keys(errors).forEach((key) => {
              if (isObject(errors[key])) {
                Object.keys(errors[key]).forEach((secondKey) => {
                  errorsArray.push(key + " - " + errors[key][secondKey][0]);
                });
              } else {
                errorsArray.push(key + " - " + errors[key]);
              }
            });
            setErrorMessage(errorsArray);
          } else {
            setErrorMessage([error.message]);
          }
        }
      });

      if (res?.status === 200) {
        setIsFormApproved(false);
        if (message.state === 2) {
          setOpenedItem({
            ...items,
            status: "Rejected",
            approved_or_rejected_by:
              storageData.first_name + " " + storageData.last_name,
          });
        } else {
          setOpenedItem({
            ...items,
            status: "Revert",
            approved_or_rejected_by:
              storageData.first_name + " " + storageData.last_name,
          });
        }

        closeConfirmationModal();
      }
    }
    updateNotifications();
  };

  const setFormItemsPharmacy = (item) => {
    // Update form information when editing PAF as a Pharmacy User Approve & Dispense

    const detailsPAFIndex = detailsPAF.findIndex(
      (element) => element.id === item.id
    );

    const detailsPAFCopy = [...detailsPAF];

    detailsPAFCopy[detailsPAFIndex] = item;

    setDetailsPAF(detailsPAFCopy);
  };

  const deletePropertyValue = (propName) => {
    const copyItems = { ...items };
    delete copyItems[propName];
    setOpenedItem(copyItems);
  };

  const closeOverduePAF = () => {
    setContinuDispenseOverduePAF((prev) => ({
      ...prev,
      isMainModalDisplayed: false,
    }));
  };
  const continueWithOverduePAF = () => {
    setContinuDispenseOverduePAF((prev) => ({
      ...prev,
      isConfirmedMainModal: true,
      isMainModalDisplayed: false,
    }));
  };

  const isDispensingPAF = () => {
    if (continuDispenseOverduePAF.isValidNonComplaintHomecareAndOutPatient) {
      setIsApproveAndDispense(true);
      setShowMahSelection(true);
      return;
    }

    const patientType = getOpenedItem().patient.patient_type;
    const errors = [];
    const openPafStatus = getOpenedItem().status || getOpenedItem().status_name;
    const isDispense7DayWarningStatus =
      openPafStatus === "Overdue" || openPafStatus === "Status Update";

    if (
      !continuDispenseOverduePAF.isConfirmedMainModal &&
      isDispense7DayWarningStatus
    ) {
      setContinuDispenseOverduePAF((prev) => ({
        ...prev,
        isMainModalDisplayed: true,
      }));
      return;
    } else if (patientType === "Woman of Childbearing Potential") {
      if (
        continuDispenseOverduePAF.isConfirmedMainModal &&
        isDispense7DayWarningStatus
      ) {
        errors.push(errorMessages.DISPENSING_MORE_THAN_7_DAYS_TITLE);
      }
    }

    if (errors.length) {
      setModalNonCompliantPAF((prevState) => ({
        ...prevState,
        isOpen: true,
        description: errors,
        screen: 1,
      }));
      return;
    }

    if (isHomecare || isOutpatient) {
      setIsApproveAndDispense(true);
      setShowMahSelection(true);
    }
  };
  const findProduct = storageData?.products?.find(
    (element) => element.product_id === getOpenedItem().product_id
  );
  // const openedPafForActions = getOpenedItem();
  // /** Top-bar actions must not use openedPAF fallback when no row is expanded. */
  // const isPafAccordionExpanded =
  //   !Array.isArray(activatedItem) && Boolean(activatedItem);
  // const openedPafStatusForRenewal =
  //   openedPafForActions?.status_name || openedPafForActions?.status;
  // const canShowRenewalPafForPrescriber =
  //   openedPafStatusForRenewal === "Dispensed" ||
  //   openedPafStatusForRenewal === "Rejected";
  // const isStatusUpdatePaf =
  //   openedPafForActions?.status === "Status Update" ||
  //   openedPafForActions?.status_name === "Status Update";
  // const isAwaitingPharmacistApproval = String(
  //   openedPafForActions?.approved_or_rejected_by ?? ""
  // ).includes("Awaiting approval");
  // // Inpatient: only offer Approve / Reject / Approve & Dispense for Submitted-style
  // // work (incl. Status Update that still shows "Awaiting approval"), not for Status
  // // Update after an approval (historic / post-dispense) — only Revert applies there.
  // const showInpatientApproveRejectTrio =
  //   openedPafForActions?.status !== "Approved" &&
  //   openedPafForActions?.status_name !== "Approved" &&
  //   (!isStatusUpdatePaf || isAwaitingPharmacistApproval);

  const hideEditButton = canInpatientPharmacistEditPaf({
    isPharmacy,
    form: openedPAF,
    findProduct,
  });

  const allConfirmed = getOpenedItem()?.prescription_confirmation_risks?.every(
    (item) => item.confirmation_risk_patient === true
  );

  if (isLoading) return <Loader />;

  const ageWarningPaf = detailsPAF.find(
    (element) => element.id === activatedItem
  );

  // Layout: MAH branch | main card | nominate branch; modals + age warning as siblings.
  return (
    <div className={PAF_PAGE_CLASS} data-testid="paf-page">
      {/* Show MahSelection modal if needed */}
      {showMahSelection ? (
        <MahSelection
          setIsOperationInProgress={setIsOperationInProgress}
          isOperationInProgress={isOperationInProgress}
          product_id={openedPAF?.product_id}
          receivedForm={getOpenedItem()}
          onPrevious={() => {
            setShowMahSelection(false);
            setIsOperationInProgress(false);
          }}
          openFinishModal={() => setFinishModalOpen(true)}
          setFinishModalContent={setFinishModalContent}
          dispensePAF={isHomecare || isOutpatient ? doDispensing : dispensePAF}
          pafID={pafID}
          setModalNonCompliantPAFWarning={setModalNonCompliantPAFWarning}
          hcp={hcp}
        />
      ) : !showNominateDispenser ? (
        <PafFormCard onClose={closeForm}>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <PafFormScrollBody
              findProduct={findProduct}
              storageData={storageData}
              navigate={navigate}
              errorMessage={errorMessage}
              latestPafId={latestPafId}
              isPharmacy={isPharmacy}
              historyPafSummaries={historyPafSummaries}
              detailsSelectedPAF={detailsSelectedPAF}
              userInfo={userInfo}
              viewPafPharmacyProps={{
                handleChangeSubmitedForm,
                numberOfCylesValues,
                isOtherNumberOfCycledValid,
                isFormApproved,
                handleAditionalOptionsChange,
                aditionalOptions,
                setAditionalOptions,
                aditionalOptionsState,
                setFormItems: setFormItemsPharmacy,
                dispensingForm,
                deletePropertyValue,
                setDetailsPAF,
                detailsPAF,
                updateChildObject,
                setCapsule_changes_tracker,
                capsule_changes_tracker,
                hideEditButton,
              }}
              viewPafDefaultProps={{
                setCapsule_changes_tracker,
                capsule_changes_tracker,
              }}
              footer={
                <PafFooterActions
                  isPrescriber={isPrescriber}
                  isConnectedNurse={isConnectedNurse}
                  findProduct={findProduct}
                  isLastFormApprovedAvailableForRenew={
                    isLastFormApprovedAvailableForRenew
                  }
                  getOpenedItem={getOpenedItem}
                  isFormApproved={isFormApproved}
                  isHomecare={isHomecare}
                  isOutpatient={isOutpatient}
                  isPharmacy={isPharmacy}
                  activePafDetails={activePafDetails}
                  onNavigateRenewalPaf={() => {
                    navigate(
                      lastFormID
                        ? App.PAF_RENEW_DEFAULT +
                        `${typeOfForm()}/${patientID}/${lastFormID}`
                        : App.PAF_RENEW_DEFAULT +
                        `${typeOfForm()}/${patientID}`
                    );
                  }}
                  onApprove={() => {
                    approveForm();
                    setIsApproveAndDispense(false);
                    setShowMahSelection(false);
                  }}
                  onApproveAndDispense={() => {
                    setIsApproveAndDispense(true);
                    setShowNominateDispenser(false);
                    approveForm();
                  }}
                  onReject={rejectForm}
                  onRevertToApprover={revertToApprover}
                  onDispense={isDispensingPAF}
                  onNavigateFinish={() => navigate(App.VIEW_PATIENTS)}
                  onDownloadPaf={downloadPafPdf}
                  canReviewPaf={canReviewPaf}
                  onReview={() => setReviewModalOpen(true)}
                />
              }
            />
          </div>
        </PafFormCard >
      ) : (
        // Show NominateDispenser modal if needed
        <NominateDispenser
          openFinishModal={() => setFinishModalOpen(true)}
          onPrevious={(shouldClosePAF = false) => {
            if (shouldClosePAF && latestPafId) setActivateTab(latestPafId);
            setShowNominateDispenser(false);
          }}
          setFinishModalContent={setFinishModalContent}
          completePAF={
            compliantPaf ? sendComplaintPAF : completeNonComplaintPAF
          }
          pafID={pafID}
          dispensingForm={dispensingForm}
          setDispensingForm={setDispensingForm}
        />
      )}

      <PafPageModals
        modalNonCompliantPAF={modalNonCompliantPAF}
        closemodalNonCompliantPAF={closemodalNonCompliantPAF}
        continueButtonNonComplainPAF={continueButtonNonComplainPAF}
        handleChangeValidPassword={handleChangeValidPassword}
        errorMessageFromServer={errorMessageFromServer}
        setValidPassword={setValidPassword}
        hcp={hcp}
        displayWeeks={displayWeeks}
        modalNonCompliantPAFWarning={modalNonCompliantPAFWarning}
        closemodalNonCompliantPAFWarning={closemodalNonCompliantPAFWarning}
        continueButtonNonComplainPAFWarning={
          continueButtonNonComplainPAFWarning
        }
        changeValuesModalIsOpen={changeValuesModalIsOpen}
        closeModalChangeValues={closeModalChangeValues}
        continueButton={continueButton}
        quitModalIsOpen={quitModalIsOpen}
        closeModalQuit={closeModalQuit}
        quitModal={quitModal}
        statePAFModal={statePAFModal}
        closeConfirmationModal={closeConfirmationModal}
        message={message}
        submitError={submitError}
        submitValidate={submitValidate}
        handleChangeSelect={handleChangeSelect}
        revert={revert}
        form={form}
        handleChange={handleChange}
        validateBeforeSending={validateBeforeSending}
        errors={errors}
        detailsPAF={detailsPAF}
        continuDispenseOverduePAF={continuDispenseOverduePAF}
        closeOverduePAF={closeOverduePAF}
        continueWithOverduePAF={continueWithOverduePAF}
        finishModalOpen={finishModalOpen}
        closeFinishModal={closeFinishModal}
        finishModalContent={finishModalContent}
      />

      <PafAgeWarningModal
        isOpen={showPAFAgeWarningModal}
        onClose={closePAFAgeWarningModal}
        onRejectAndRequest={() => {
          closePAFAgeWarningModal();
          rejectForm();
        }}
        pfaIssuedDate={ageWarningPaf?.pfa_issued}
      />

      <PrescriptionReviewModal
        open={reviewModalOpen}
        prescriptionId={pafID || activePafDetails?.id}
        onClose={() => setReviewModalOpen(false)}
        onSuccess={handlePrescriptionReviewSuccess}
      />

    </div >
  );
};
export default PrescriptionAuthorisationForm;
